import React, { useEffect, useState } from "react";
import { Avatar, Box, Button, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import ShortcutRoundedIcon from "@mui/icons-material/ShortcutRounded";
import EmailProviderIcon, { emailProviderLabel, inferEmailProvider } from "./EmailProviderIcon";
import MessageAttachmentGallery from "./MessageAttachmentGallery";

const INBOX_TIME_ZONE = "Africa/Harare";

const formatTimestamp = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();

  if (sameDay) {
    return new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
      timeZone: INBOX_TIME_ZONE,
    }).format(date);
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    timeZone: INBOX_TIME_ZONE,
  }).format(date);
};

const formatFullTimestamp = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "full",
    timeStyle: "short",
    timeZone: INBOX_TIME_ZONE,
  }).format(date);
};

const messageInitials = (name, email) => {
  const seed = String(name || email || "E").trim();
  return seed.split(/\s+/).map((part) => part[0] || "").join("").slice(0, 2).toUpperCase();
};

const extractEmailHtml = (html) => {
  const raw = String(html || "").trim();
  if (!raw) return "";
  const withoutScripts = raw.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
  const bodyMatch = withoutScripts.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  return (bodyMatch?.[1] || withoutScripts).trim();
};

const stripHtmlToText = (html) => {
  const raw = String(html || "").trim();
  if (!raw) return "";
  return raw.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
};

const resolveEmailProvider = (conversation, message) =>
  inferEmailProvider({
    provider: message?.metadata?.provider || conversation?.metadata?.provider,
    metadata: message?.metadata,
    mailbox: conversation?.mailbox,
    user_mailbox_id: conversation?.mailbox?.id,
  });

const formatHeaderEmail = (name, email) => {
  const safeName = String(name || "").trim();
  const safeEmail = String(email || "").trim();
  if (safeName && safeEmail) return `${safeName} <${safeEmail}>`;
  return safeName || safeEmail || "-";
};

const resolveRecipientLine = (conversation, message, inbound) => {
  const headerTo = String(message?.metadata?.to || "").trim();
  if (headerTo) return headerTo;
  if (inbound) {
    return conversation?.mailbox?.email_address || message?.metadata?.mailbox_email || "Company mailbox";
  }
  return conversation?.customer?.email || conversation?.contact?.email || "Customer";
};

const buildDefaultExpandedIds = (messages = []) => {
  const lastEmail = [...messages].reverse().find(
    (message) => message.message_type !== "system"
  );

  return lastEmail ? new Set([lastEmail.id]) : new Set();
};

function EmailBody({ body }) {
  const html = extractEmailHtml(body?.html);

  if (html) {
    return (
      <Box
        sx={{
          "& p": { mt: 0, mb: 1.25 },
          "& table": { maxWidth: "100%" },
          "& blockquote": {
            margin: "14px 0",
            pl: 1.5,
            borderLeft: "3px solid #D7E4DC",
            color: "#667085",
          },
          "& a": { color: "#1A73E8" },
          "& img": { maxWidth: "100%" },
          "& hr": { border: "none", borderTop: "1px solid #E3ECE7", margin: "18px 0" },
          color: "#173127",
          fontSize: 14,
          lineHeight: 1.75,
          wordBreak: "break-word",
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  if (body?.text) {
    return (
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", color: "#173127", lineHeight: 1.75 }}>
        {body.text}
      </Typography>
    );
  }

  return null;
}

// ── Avatar colour stable per sender ──────────────────────────────────────────
const AV_PALETTES = [
  { bg: "#e8f0fe", color: "#1A73E8" },
  { bg: "#dcfce7", color: "#166534" },
  { bg: "#fef3c7", color: "#92400e" },
  { bg: "#ede9fe", color: "#5b21b6" },
  { bg: "#fce7f3", color: "#9d174d" },
  { bg: "#e0f2fe", color: "#0369a1" },
];
const avatarPalette = (seed) => AV_PALETTES[(String(seed || "A").charCodeAt(0) || 0) % AV_PALETTES.length];

// ── Single collapsed row (Gmail list style) ───────────────────────────────────
function CollapsedRow({ message, conversation, onExpand }) {
  const inbound = message.direction === "inbound";
  const internalNote = message.message_type === "internal_note";
  const systemEvent = message.message_type === "system";
  const senderName = message.sender?.name || (inbound ? "Sender" : "Team");
  const pal = avatarPalette(senderName);
  const preview = (() => {
    const html = extractEmailHtml(message.body?.html);
    const raw = html ? stripHtmlToText(html) : String(message.body?.text || "").trim();
    return raw ? raw.slice(0, 90) + (raw.length > 90 ? "…" : "") : "(no content)";
  })();

  if (systemEvent) {
    return (
      <Box
        onClick={onExpand}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          px: 2,
          py: 0.9,
          cursor: "pointer",
          borderRadius: "4px",
          "&:hover": { bgcolor: "#F3F7F4" },
        }}
      >
        <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: "#EEF2F6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#94A3B8" }} />
        </Box>
        <Typography variant="caption" sx={{ color: "#94A3B8", fontStyle: "italic", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          System · {preview}
        </Typography>
        <Typography variant="caption" sx={{ color: "#94A3B8", flexShrink: 0 }}>{formatTimestamp(message.occurred_at)}</Typography>
      </Box>
    );
  }

  return (
    <Box
      onClick={onExpand}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.4,
        px: 2,
        py: "10px",
        cursor: "pointer",
        borderRadius: 2,
        bgcolor: internalNote ? alpha("#FFF8E1", 0.6) : "transparent",
        "&:hover": { bgcolor: internalNote ? "#FFF8E1" : "#F3F7F4" },
      }}
    >
      <Avatar sx={{ width: 32, height: 32, fontSize: 11, fontWeight: 700, bgcolor: internalNote ? "#FEF9C3" : pal.bg, color: internalNote ? "#92400e" : pal.color, flexShrink: 0 }}>
        {messageInitials(senderName, message.sender?.email)}
      </Avatar>

      <Typography variant="body2" sx={{ fontWeight: 700, color: "#173127", flexShrink: 0, minWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }}>
        {internalNote ? `📝 ${senderName}` : senderName}
      </Typography>

      <Typography variant="caption" sx={{ color: "#708277", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {preview}
      </Typography>

      {(message.attachments || []).length > 0 && (
        <AttachFileRoundedIcon sx={{ fontSize: 14, color: "#94A3B8", flexShrink: 0 }} />
      )}

      <Typography variant="caption" sx={{ color: "#94A3B8", flexShrink: 0, ml: 0.5 }}>
        {formatTimestamp(message.occurred_at)}
      </Typography>
    </Box>
  );
}

// ── Single expanded message (Gmail card style) ────────────────────────────────
function ExpandedMessage({ message, conversation, onCollapse, isLast, onReply }) {
  const inbound = message.direction === "inbound";
  const internalNote = message.message_type === "internal_note";
  const systemEvent = message.message_type === "system";
  const senderName = message.sender?.name || (inbound ? "Sender" : "Team");
  const senderEmail = message.sender?.email || "";
  const provider = resolveEmailProvider(conversation, message);
  const providerLabel = emailProviderLabel(provider);
  const pal = avatarPalette(senderName);

  const headerBg = internalNote
    ? "#FFFDE7"
    : systemEvent
      ? "#F8FAFC"
      : "#FFFFFF";

  const bodyBg = internalNote
    ? "#FFFEF5"
    : systemEvent
      ? "#F8FAFC"
      : inbound
        ? "#FFFFFF"
        : "#F9FCFA";

  return (
    <Box
      sx={{
        borderRadius: "6px",
        border: "1px solid",
        borderColor: internalNote ? "#FEF08A" : systemEvent ? "#E2E8F0" : "#E3ECE7",
        overflow: "hidden",
        bgcolor: bodyBg,
        boxShadow: isLast ? "0 4px 18px -12px rgba(15,23,42,0.16)" : "none",
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ px: 2.2, py: 1.5, bgcolor: headerBg }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          {/* Left: avatar + meta */}
          <Stack direction="row" spacing={1.4} sx={{ minWidth: 0, flex: 1 }}>
            <Avatar
              sx={{
                width: 38,
                height: 38,
                fontSize: 13,
                fontWeight: 700,
                flexShrink: 0,
                bgcolor: internalNote ? "#FEF9C3" : pal.bg,
                color: internalNote ? "#92400e" : pal.color,
              }}
            >
              {messageInitials(senderName, senderEmail)}
            </Avatar>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              {/* Sender name row */}
              <Stack direction="row" spacing={0.8} alignItems="center" useFlexGap flexWrap="wrap">
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#173127" }}>
                  {internalNote ? `📝 ${senderName}` : senderName}
                </Typography>
                {!systemEvent && !internalNote && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <EmailProviderIcon provider={provider} size={13} />
                    <Typography variant="caption" sx={{ color: "#94A3B8", fontSize: 11 }}>
                      {providerLabel}
                    </Typography>
                  </Stack>
                )}
                {systemEvent && (
                  <Typography variant="caption" sx={{ color: "#94A3B8", fontStyle: "italic" }}>
                    System event
                  </Typography>
                )}
              </Stack>

              {/* From / To compact line */}
              {!systemEvent && (
                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                  <Typography variant="caption" sx={{ color: "#708277", display: "block", wordBreak: "break-word" }}>
                    <Box component="span" sx={{ fontWeight: 700, color: "#173127" }}>from </Box>
                    {formatHeaderEmail(senderName, senderEmail || message.sender?.phone)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#708277", display: "block", wordBreak: "break-word" }}>
                    <Box component="span" sx={{ fontWeight: 700, color: "#173127" }}>to </Box>
                    {resolveRecipientLine(conversation, message, inbound)}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>

          {/* Right: timestamp + collapse button */}
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
            <Tooltip title={formatFullTimestamp(message.occurred_at)} placement="top">
              <Typography variant="caption" sx={{ color: "#94A3B8", whiteSpace: "nowrap" }}>
                {formatTimestamp(message.occurred_at)}
              </Typography>
            </Tooltip>
            <Tooltip title="Collapse">
              <IconButton size="small" onClick={onCollapse} sx={{ color: "#94A3B8", width: 26, height: 26 }}>
                <ExpandLessRoundedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      <Divider sx={{ borderColor: internalNote ? "#FEF08A" : "#E3ECE7" }} />

      {/* ── Body ── */}
      <Box sx={{ px: 2.2, pt: 1.8, pb: isLast && !internalNote && !systemEvent ? 1 : 1.8 }}>
        <EmailBody body={message.body} />
        {!message?.body?.html && !message?.body?.text && !(message?.attachments || []).length ? (
          <Typography variant="body2" sx={{ color: "#94A3B8", fontStyle: "italic" }}>
            No email body
          </Typography>
        ) : null}
        <MessageAttachmentGallery attachments={message.attachments || []} />
      </Box>

      {/* ── Gmail-style Reply / Forward buttons (last message only) ── */}
      {isLast && !internalNote && !systemEvent && (
        <>
          <Divider sx={{ borderColor: "#F0F4F1", mx: 2.2 }} />
          <Box sx={{ px: 2.2, py: 1.2, display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ReplyRoundedIcon sx={{ fontSize: 15 }} />}
              onClick={onReply}
              sx={{
                borderRadius: "18px",
                borderColor: "#CBD5E1",
                color: "#475569",
                fontSize: 12,
                fontWeight: 600,
                px: 1.6,
                py: 0.4,
                textTransform: "none",
                "&:hover": { borderColor: "#94A3B8", bgcolor: "#F8FAFC" },
              }}
            >
              Reply
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ShortcutRoundedIcon sx={{ fontSize: 15 }} />}
              onClick={onReply}
              sx={{
                borderRadius: "18px",
                borderColor: "#CBD5E1",
                color: "#475569",
                fontSize: 12,
                fontWeight: 600,
                px: 1.6,
                py: 0.4,
                textTransform: "none",
                "&:hover": { borderColor: "#94A3B8", bgcolor: "#F8FAFC" },
              }}
            >
              Forward
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

// ── Main thread view ──────────────────────────────────────────────────────────
export default function EmailThreadView({
  conversation = null,
  messages = [],
  onReply = null,
  emptyLabel = "No email messages have been captured for this thread yet.",
}) {
  // Last message is expanded by default; all others collapsed.
  const [expandedIds, setExpandedIds] = useState(() => buildDefaultExpandedIds(messages));

  useEffect(() => {
    setExpandedIds((prev) => {
      const currentMessageIds = new Set(messages.map((message) => message.id));
      const stillHasExpandedMessage = [...prev].some((id) => currentMessageIds.has(id));

      if (stillHasExpandedMessage) {
        return prev;
      }

      return buildDefaultExpandedIds(messages);
    });
  }, [conversation?.id, messages]);

  if (!messages.length) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      </Box>
    );
  }

  const toggle = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const lastIndex = messages.length - 1;

  return (
    <Box sx={{ px: 2, py: 2, display: "flex", flexDirection: "column", gap: 0 }}>
      {messages.map((message, index) => {
        const isLast = index === lastIndex;
        const expanded = expandedIds.has(message.id);

        return (
          <Box key={message.id}>
            {expanded ? (
              <ExpandedMessage
                message={message}
                conversation={conversation}
                onCollapse={() => toggle(message.id)}
                isLast={isLast}
                onReply={onReply}
              />
            ) : (
              <CollapsedRow
                message={message}
                conversation={conversation}
                onExpand={() => toggle(message.id)}
              />
            )}
            {/* Thin separator between items (not after the last) */}
            {!isLast && !expanded && (
              <Box
                sx={{
                  mx: 2,
                  borderBottom: "1px solid #F0F4F1",
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
}
